import { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useLazyGetPathQuery } from '../services/edgeApi';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllNodes,
  selectAllPoints,
  selectEdgeLookup,
} from '../map/slice/mapSelectors';
import { mapActions } from '../map/slice/mapSlice';
import { generateColor } from '../utils/colors';
import { utils, writeFileXLSX } from 'xlsx';
import { Edge, Point, SegmentFlowDirection } from '@/types';
import { tuplesToList } from '../utils/objectUtils';

export const usePathBuilder = () => {
  const dispatch = useDispatch();
  const [getPath] = useLazyGetPathQuery();

  const allNodes = useSelector(selectAllNodes);
  const allPoints = useSelector(selectAllPoints);
  const edgeLookup = useSelector(selectEdgeLookup);

  const [points, setPoints] = useState<Point[]>([]);
  const [newPointPosition, setNewPointPosition] = useState<number | null>(null);
  const [path, setPath] = useState<Record<string, [number, number][]> | null>(
    null,
  );

  /** -----------------------------------------------------
   * POINT MANAGEMENT
   * ----------------------------------------------------*/

  const addPoints = (pointsToAdd: Point[], index: number) => {
    setPoints((p) => {
      if (
        pointsToAdd[0].id === p[index]?.id ||
        pointsToAdd[0].id === p[index - 1]?.id
      ) {
        toast.error('Point already exists in this position');
        return p;
      }
      const newPoints = [...p];
      newPoints.splice(index, 0, ...pointsToAdd);
      return newPoints;
    });
  };

  const removePoint = (index: number) => {
    setPoints((p) => {
      const copy = [...p];
      copy.splice(index, 1);
      return copy;
    });
  };

  const clearPoints = () => {
    setPoints([]);
  };

  /** -----------------------------------------------------
   * API FETCHING BASED ON POINTS
   * ----------------------------------------------------*/
  const fetchPath = () => {
    if (points.length < 2) {
      setPath(null);
      return;
    }

    getPath({ nodes: points.filter((p) => p.node).map((p) => p.node!) })
      .unwrap()
      .then((data) => setPath(data))
      .catch(() => toast.error('Failed to fetch path'));
  };

  useEffect(fetchPath, [points]);

  /** -----------------------------------------------------
   * MAP DISPLAY SYNC
   * ----------------------------------------------------*/
  useEffect(() => {
    dispatch(
      mapActions.setPaths(
        Object.values(path ?? {}).map((nodes, idx, all) => {
          const factor = 0.2 + ((idx + 1) / all.length) * 0.8;
          return {
            nodes: tuplesToList(nodes),
            color: generateColor(2, 80, factor * 50),
          };
        }),
      ),
    );
  }, [path]);

  /** -----------------------------------------------------
   * TRANSFORM RAW NODE IDS INTO FULL UI-READY PATH DATA
   * ----------------------------------------------------*/
  const pathData = useMemo(() => {
    return Object.entries(path ?? {}).reduce<
      Array<{ from: Point; to: Point; path: [Edge, SegmentFlowDirection][] }>
    >((data, [key, nodes]) => {
      const [startNodeId, endNodeId] = key.split('-');
      const startNode = allNodes[startNodeId];
      const endNode = allNodes[endNodeId];
      const startPoint = allPoints[startNode?.points[0]];
      const endPoint = allPoints[endNode?.points[0]];

      const pathEdges = nodes
        .map(
          ([fromNodeId, toNodeId]) =>
            edgeLookup[`${fromNodeId}-${toNodeId}`] || null,
        )
        .filter(Boolean);

      data.push({
        from: startPoint,
        to: endPoint,
        path: pathEdges,
      });

      return data;
    }, []);
  }, [path, allNodes, allPoints, edgeLookup]);

  /** -----------------------------------------------------
   * DOWNLOAD (EXCEL EXPORT)
   * ----------------------------------------------------*/
  const downloadTable = () => {
    if (points.length < 2) {
      toast.error('At least two points are required');
      return;
    }

    const fields = [
      'Location Name',
      'Location ID',
      'Flow Direction',
      'County',
      'Type',
      'Receipt Zone',
      'Delivery Zone',
      'Seg Nbr',
      'Up/Dn Loc Name',
    ];

    const rows = pathData.flatMap(({ path }, pathIndex, pathList) =>
      path.flatMap(([edge, direction], edgeIdx, edgeList) => {
        const nodePoints = edge.start_points
          .map((id) => allPoints[id])
          .filter(Boolean);
        const capacities =
          direction === 'FORWARDHAUL'
            ? edge.forwardhaul_capacity_components
            : edge.backhaul_capacity_components.concat(
                edge.forwardhaul_capacity_components,
              );

        const pointRows = nodePoints.map((point) => [
          point.name,
          point.loc_id,
          point.dir_flo,
          point.loc_cnty,
          point.loc_type_ind,
          point.receipt_zone_name,
          point.delivery_zone_name,
          point.seg_nbr,
          point.up_dn_loc_name,
        ]);

        const capacityRows = capacities.map(([_, capName]) => [
          capName,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ]);

        // Add end node's points only on the final segment
        if (
          pathIndex === pathList.length - 1 &&
          edgeIdx === edgeList.length - 1
        ) {
          const endNodePoints = edge.end_points
            .map((id) => allPoints[id])
            .filter(Boolean);
          const endRows = endNodePoints.map((point) => [
            point.name,
            point.loc_id,
            point.dir_flo,
            point.loc_cnty,
            point.loc_type_ind,
            point.receipt_zone_name,
            point.delivery_zone_name,
            point.seg_nbr,
            point.up_dn_loc_name,
          ]);
          return [...pointRows, ...capacityRows, ...endRows];
        }

        return [...pointRows, ...capacityRows];
      }),
    );

    const ws = utils.json_to_sheet([fields, ...rows], { skipHeader: true });
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Path');
    writeFileXLSX(
      wb,
      `from-${points[0]?.name.replace(/\s/g, '_')}-to-${points[
        points.length - 1
      ].name.replace(/\s/g, '_')}.xlsx`,
    );
  };

  return {
    points,
    setPoints,
    addPoints,
    removePoint,
    clearPoints,
    newPointPosition,
    setNewPointPosition,
    path,
    pathData,
    downloadTable,
  };
};
